import { bindPepperjam as requestBindPepperjam } from '@/api/account';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { getSetLocalStorage } from '@/utils/tools';
import { Provider, ProviderType, TrackingEvent } from '../types';

const PEPPERJAM_LS_PUBLISHER = 'pepperjam_publisher_id';
const PEPPERJAM_LS_CLICK = 'pepperjam_click_id';
const PEPPERJAM_LS_UTM_CAMPAIGN = 'pepperjam_utm_campaign';
const PEPPERJAM_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface PepperjamConfig {
  containerId: string;
  isEnabled?: boolean;
}

// Window interface已在index.d.ts中统一定义

export class PepperjamProvider implements Provider {
  type = ProviderType.PEPPERJAM;
  isEnabled: boolean;
  loggingEnabled = false; // 公开属性，支持ReportingCenter访问
  private initialized = false;
  private config: PepperjamConfig;

  constructor(config: PepperjamConfig) {
    this.config = config;
    this.isEnabled = config.isEnabled ?? true;
    this.loggingEnabled = env.NEXT_PUBLIC_ENV === 'local';
  }

  async init(): Promise<boolean> {
    try {
      if (typeof window === 'undefined') return false;
      if (this.initialized) return true;

      // Initialize Pepperjam tracking using the original script logic
      // 为了减小对TBT的影响，把其加载的时序再往后延
      setTimeout(() => {
        // Replicate the original Pepperjam script
        const a = document.createElement('script');
        a.type = 'text/javascript';
        a.async = true;
        a.src = `//container.pepperjam.com/${this.config.containerId}.js`;

        a.onload = () => {
          if (this.loggingEnabled) {
            logger.log('Pepperjam: Script loaded successfully');
          }
        };

        a.onerror = () => {
          if (this.loggingEnabled) {
            logger.error('Failed to load Pepperjam script');
          }
        };

        // Try to append to head first, fallback to inserting before first script
        const head = document.getElementsByTagName('head')[0];
        if (head) {
          head.appendChild(a);
        } else {
          const firstScript = document.getElementsByTagName('script')[0];
          if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(a, firstScript);
          } else {
            // Fallback: append to document body
            document.body.appendChild(a);
          }
        }
      }, 5000);
      this.initialized = true;
      return true;
    } catch (error) {
      logger.error('Failed to initialize Pepperjam:', error);
      return false;
    }
  }

  track(event: TrackingEvent): void {
    if (!this.initialized || typeof window === 'undefined') return;

    try {
      if (this.loggingEnabled) {
        logger.log('Pepperjam tracking event:', event);
      }
      // 暂时为空，没有在主站看到有使用的地方
    } catch (error) {
      logger.error('Pepperjam tracking error:', error);
    }
  }

  /**
   * 联盟落地链接通常同时带 publisher、clickId（或 click）、utm_campaign。
   * 合并当前 URL 与本地缓存；三项齐全则整包写入并续期 30 天（站内无参页登录时仍可从缓存绑定）。
   */
  async bindPepperjam(token?: string, onSuccess?: () => void): Promise<void> {
    if (typeof window === 'undefined' || !this.isEnabled) return;

    const urlParams = new URLSearchParams(window.location.search);
    const expireAt = Date.now() + PEPPERJAM_ATTRIBUTION_TTL_MS;

    const fromUrlPublisher = urlParams.get('publisher');
    const fromUrlClick = urlParams.get('click');
    const fromUrlCampaign = urlParams.get('utm_campaign');

    const publisher_id = fromUrlPublisher || getSetLocalStorage.get(PEPPERJAM_LS_PUBLISHER) || '';
    const click_id = fromUrlClick || getSetLocalStorage.get(PEPPERJAM_LS_CLICK) || '';
    const utm_campaign = fromUrlCampaign || getSetLocalStorage.get(PEPPERJAM_LS_UTM_CAMPAIGN) || '';

    if (!publisher_id || !click_id || !utm_campaign) {
      return;
    }

    getSetLocalStorage.set(PEPPERJAM_LS_PUBLISHER, publisher_id, expireAt);
    getSetLocalStorage.set(PEPPERJAM_LS_CLICK, click_id, expireAt);
    getSetLocalStorage.set(PEPPERJAM_LS_UTM_CAMPAIGN, utm_campaign, expireAt);

    if (!token) {
      return;
    }

    try {
      const bindRes = await requestBindPepperjam({ publisher_id, click_id, utm_campaign });
      if (bindRes?.code === 200) {
        onSuccess?.();
        localStorage.removeItem(PEPPERJAM_LS_PUBLISHER);
        localStorage.removeItem(PEPPERJAM_LS_CLICK);
        localStorage.removeItem(PEPPERJAM_LS_UTM_CAMPAIGN);
      }
    } catch (e) {
      logger.error('bindPepperjam error', e);
    }
  }

  /**
   * 启用Pepperjam日志
   */
  enableLog(): void {
    this.loggingEnabled = true;
  }

  /**
   * 禁用Pepperjam日志
   */
  disableLog(): void {
    this.loggingEnabled = false;
  }

  isInitialized(): boolean {
    return this.initialized;
  }
}
